import 'select2';
import 'select2/dist/css/select2';
import { Controller } from "stimulus";
import BookEntriesService from "../entrypoints/src/admin/services/book-entries-service";
import { get } from "@rails/request.js"
import { isEmpty } from 'lodash';

export default class extends Controller {
  static targets = ['termOfPaymentInput', 'isPaidInput', 'whenOneTime',
    'whenInstallments', 'whenPaid', 'whenFixedCost', 'whenRecurrence',
    'whenOneTimeAndRecurrence', 'valueInput', 'installmentValueInput', 'installmentsContainer',
    'installmentAmountDisplay', 'entryableTypeInput', 'resourceEntryableInput', 'recurrenceTypeInput',
    'startOccurrenceDateInput', 'recurrenceTargetInput', 'occurrenceCountInput', 'nameInput',
    'summaryBase', 'summaryDiscount', 'summaryInterest', 'summaryFine',
    'summaryDiscountWrap', 'summaryInterestWrap', 'summaryFineWrap',
    'summaryDetail', 'summaryTotal', 'summaryStatus', 'summaryStatusLabel',
    'paidToggleLabel', 'paidToggleHint']

  static values = { installmentsAmount: Number, bookEntryId: String, formAction: String, flow: String };

  connect() {
    this.onChangePaidInput();
    if (this.hasInstallmentsContainerTarget) { this.handleInstallmentAmount() }
    if (this.hasTermOfPaymentInputTarget)    { this.onChangeTermOfPayment()   }

    this.handleModals();
    this.searchResources();
    this.recomputeSummary();
  }

  setTermOfPayment(event) {
    if (!this.hasTermOfPaymentInputTarget) return;
    const value = event.currentTarget.dataset.term;
    this.termOfPaymentInputTarget.value = value;
    this.element.querySelectorAll('[data-book-entries-form-term-button]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.term === value);
    });
    this.onChangeTermOfPayment();
    this.recomputeSummary();
  }

  recomputeSummary() {
    if (this._pendingRecompute) return;
    this._pendingRecompute = true;
    requestAnimationFrame(() => {
      this._pendingRecompute = false;
      this._doRecomputeSummary();
    });
  }

  _doRecomputeSummary() {
    if (!this.hasSummaryTotalTarget || !this.hasValueInputTarget) return;

    const flow = this.hasFlowValue ? this.flowValue : 'expense';
    const isOneTime = this.hasTermOfPaymentInputTarget ? this.termOfPaymentInputTarget.value === 'one_time' : true;

    const base = this.parseMoney(this.valueInputTarget.value);
    const fields = this.fieldsByName([
      'book_entry[discount_value]',
      'book_entry[interest_value]',
      'book_entry[fines_value]',
      'book_entry[maturity_date]',
      'book_entry[payment_value]'
    ]);
    const discountRaw = isOneTime ? this.parseMoney(fields[0]?.value) : 0;
    const interestRaw = isOneTime ? this.parseMoney(fields[1]?.value) : 0;
    const fineRaw     = isOneTime ? this.parseMoney(fields[2]?.value) : 0;
    const maturity    = this.parseBrDate(fields[3]?.value);
    const paymentValue = this.parseMoney(fields[4]?.value);

    const isPaid = this.hasIsPaidInputTarget && this.isPaidInputTarget.checked;
    const today = this.startOfToday();
    const isOverdue = !isPaid && maturity && maturity < today;

    // Componentes efetivos: pré-vencimento aplica desconto, pós-vencimento aplica juros+multa.
    // Liquidado mostra o valor efetivamente recebido/pago.
    let total, discount, interest, fine, statusKey;
    if (isPaid) {
      total = paymentValue > 0 ? paymentValue : Math.max(0, base - discountRaw + interestRaw + fineRaw);
      discount = 0; interest = 0; fine = 0;
      statusKey = 'paid';
    } else if (isOverdue) {
      discount = 0;
      interest = interestRaw;
      fine = fineRaw;
      total = Math.max(0, base + interest + fine);
      statusKey = 'overdue';
    } else {
      discount = discountRaw;
      interest = 0;
      fine = 0;
      total = Math.max(0, base - discount);
      statusKey = 'open';
    }

    this.summaryBaseTarget.textContent = this.formatBrl(base);
    if (this.hasSummaryDiscountTarget) this.summaryDiscountTarget.textContent = this.formatBrl(discount);
    if (this.hasSummaryInterestTarget) this.summaryInterestTarget.textContent = this.formatBrl(interest);
    if (this.hasSummaryFineTarget) this.summaryFineTarget.textContent = this.formatBrl(fine);

    const showExtras = discount > 0 || interest > 0 || fine > 0;
    if (this.hasSummaryDetailTarget) {
      this.summaryDetailTarget.classList.toggle('hidden', !showExtras);
      this.summaryDetailTarget.classList.toggle('flex', showExtras);
    }
    if (this.hasSummaryDiscountWrapTarget) this.summaryDiscountWrapTarget.classList.toggle('hidden', discount <= 0);
    if (this.hasSummaryInterestWrapTarget) this.summaryInterestWrapTarget.classList.toggle('hidden', interest <= 0);
    if (this.hasSummaryFineWrapTarget) this.summaryFineWrapTarget.classList.toggle('hidden', fine <= 0);

    this.summaryTotalTarget.textContent = this.formatBrl(total);
    this.summaryTotalTarget.classList.toggle('text-green-600', flow === 'income');
    this.summaryTotalTarget.classList.toggle('text-red-700', flow !== 'income');

    this._renderSummaryStatus({ statusKey, discountRaw, interestRaw, fineRaw, flow });
  }

  _renderSummaryStatus({ statusKey, discountRaw, interestRaw, fineRaw, flow }) {
    if (!this.hasSummaryStatusTarget) return;

    const flowVerb = flow === 'income' ? 'receber' : 'pagar';
    let label = '';
    let tone = 'neutral';

    if (statusKey === 'paid') {
      label = `Liquidado · valor exibido é o efetivamente ${flow === 'income' ? 'recebido' : 'pago'}`;
      tone = 'success';
    } else if (statusKey === 'overdue') {
      const parts = [];
      if (discountRaw > 0) parts.push('desconto ignorado');
      if (interestRaw > 0 || fineRaw > 0) parts.push('juros e multa aplicados');
      label = `Vencido · ${parts.join(' · ') || `valor cheio será cobrado para ${flowVerb}`}`;
      tone = 'danger';
    } else {
      const willApplyLater = interestRaw > 0 || fineRaw > 0;
      if (willApplyLater) {
        label = 'No prazo · juros e multa só serão aplicados após o vencimento';
        tone = 'warning';
      } else {
        label = '';
      }
    }

    if (this.hasSummaryStatusLabelTarget) this.summaryStatusLabelTarget.textContent = label;

    const toneClasses = {
      success: ['bg-green-50',  'text-green-700',  'border-green-200'],
      danger:  ['bg-red-50',    'text-red-700',    'border-red-200'],
      warning: ['bg-amber-50',  'text-amber-700',  'border-amber-200'],
      neutral: ['bg-gray-50',   'text-gray-600',   'border-gray-200']
    };
    Object.values(toneClasses).flat().forEach((cls) => this.summaryStatusTarget.classList.remove(cls));
    toneClasses[tone].forEach((cls) => this.summaryStatusTarget.classList.add(cls));
    this.summaryStatusTarget.classList.toggle('hidden', !label);
    this.summaryStatusTarget.classList.toggle('inline-flex', !!label);
  }

  parseBrDate(raw) {
    if (!raw) return null;
    const match = String(raw).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const [, d, m, y] = match;
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  startOfToday() {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }

  // Values arrive masked as "1.234.567,89" (jQuery mask, cents-first). Strip
  // every non-digit character and treat the integer as cents — matches the
  // existing parsing used in updateValueForInstallments.
  parseMoney(raw) {
    if (raw === '' || raw == null) return 0;
    const digits = String(raw).replace(/\D/g, '');
    return digits ? parseInt(digits, 10) / 100 : 0;
  }

  formatBrl(num) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num || 0);
  }

  fieldsByName(names) {
    return names.map((name) => this.element.querySelector(`[name="${name}"]`));
  }

  onChangePaidInput(event) {
    if (!this.hasIsPaidInputTarget) return;
    const checked = this.isPaidInputTarget.checked;
    if (checked) {
      this.whenPaidTargets.forEach(($el) => {
        if ($el.dataset.disabled == 'true') { return }
        $el.removeAttribute('disabled');
      });
      // Prefill payment date and value when marking as settled. Only on user
      // interaction (event present) and without overwriting values already set
      // — connect() calls without an event and must not touch them.
      if (event) { this.prefillPaymentFields(); }
    } else {
      this.whenPaidTargets.forEach(($el) => {
        if ($el.dataset.disabled == 'true') { return }
        $el.setAttribute('disabled', true);
      });
    }
    if (this.hasPaidToggleLabelTarget) {
      this.paidToggleLabelTarget.textContent = checked ? 'Liquidado' : 'Em aberto';
    }
    this.recomputeSummary();
  }

  // When settling, suggest today's date and the book_entry base value as the
  // payment value, keeping anything the user already filled in.
  prefillPaymentFields() {
    const [paymentDate, paymentValue] = this.fieldsByName([
      'book_entry[payment_date]',
      'book_entry[payment_value]'
    ]);

    if (paymentDate && paymentDate.dataset.disabled != 'true' && isEmpty(paymentDate.value)) {
      paymentDate.value = this.todayBrDate();
    }

    // money-mask initializes the field as "0,00" (not empty), so we compare the
    // parsed value instead of isEmpty — only suggest when still zeroed.
    if (paymentValue && paymentValue.dataset.disabled != 'true' && this.parseMoney(paymentValue.value) <= 0 && this.hasValueInputTarget) {
      paymentValue.value = this.valueInputTarget.value;
    }
  }

  todayBrDate() {
    const t = new Date();
    const dd = String(t.getDate()).padStart(2, '0');
    const mm = String(t.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${t.getFullYear()}`;
  }

  onChangeTermOfPayment() {
    this.handleDivsForTermOfPayment();
    this.handleInputsForTermOfPayment();
  }

  searchResources() {
    const opts = {
      placeholder: 'Digite pelo menos 4 caracteres para buscar',
      minimumInputLength: 4,
      language: {
        inputTooShort: () => 'Por favor, insira 4 ou mais caracteres',
        searching: () => 'Buscando...',
        noResults: () => 'Nenhum resultado encontrado'
      },
      ajax: {
        transport: async (params, success, failure) => {
          try {
            const result = await BookEntriesService.searchResourceEntryable(params.data.term);
            const formattedResult = result.map((i) => { return { id: i.id, text: i.name, type: i.type, type_label: i.type_label || i.type }});
            const groupedFormattedResult = Object.groupBy(formattedResult, ({type_label}) => type_label);
            let optionsGroupedResult = [];
            Object.keys(groupedFormattedResult).forEach((key) => {
              optionsGroupedResult.push({ text: key, children: groupedFormattedResult[key]})
            })
            success({ results: optionsGroupedResult });
          } catch (error) {
            console.error("Erro ao buscar a lista de pessoas:", error);
            failure();
          }
        },
        processResults: (data) => {
          return {
            results: data.results
          };
        },
        delay: 100
      }
    };

    const modal = $(this.element).find('.modal');
    if (modal.length) {
      opts['dropdownParent'] = modal;
    }

    $('.entryable-select2').select2(opts)
      .on('select2:select', (e) => {
      this.entryableTypeInputTarget.value = e.params.data.type
    });

    if (this.hasResourceEntryableInputTarget) { this.handleResourceEntryableOnEdit() }
  }

  handleDivsForTermOfPayment() {
    const value = this.termOfPaymentInputTarget.value;
    this.whenOneTimeTargets.forEach(($el) => $el.classList.toggle('hidden', value !== 'one_time'));
    this.whenInstallmentsTargets.forEach(($el) => $el.classList.toggle('hidden', value !== 'in_installments'));
    this.whenRecurrenceTargets.forEach(($el) => $el.classList.toggle('hidden', value !== 'recurrence'));
    this.whenOneTimeAndRecurrenceTargets.forEach(($el) => $el.classList.toggle('hidden', !['recurrence', 'one_time'].includes(value)));
  }

  handleInputsForTermOfPayment() {
    if (['recurrence', 'one_time'].includes(this.termOfPaymentInputTarget.value)) {
      this.valueInputTarget.removeAttribute('readonly');
    } else {
      this.valueInputTarget.setAttribute('readonly', true);
    }
  }

  handleInstallmentAmount() {
    this.element.querySelector('a.add-row').addEventListener('add-row:appended', () => {
      this.installmentsAmountValue += 1;
      this.installmentAmountDisplayTargets.forEach(($el, index) => $el.textContent = (index + 1) + '/' + this.installmentsAmountValue );
    });

    this.installmentsContainerTarget.addEventListener('remove-row:detached', () => {
      this.installmentsAmountValue -= 1;
      this.installmentAmountDisplayTargets.forEach(($el, index) => $el.textContent = (index + 1) + '/' + this.installmentsAmountValue );
    })
  }

  handleModals() {
    const opts = {};
    const modal = $(this.element).find('.modal');
    if (modal.length) {
      opts['dropdownParent'] = modal;
    }

    $('.select2').select2(opts);
  }

  async handleResourceEntryableOnEdit() {
    let resourceInput = $('.entryable-select2');
    let resourceEntryable = await BookEntriesService.getResourceEntryable(this.bookEntryIdValue)
    if (resourceEntryable.id === null) { return }

    let option = new Option(resourceEntryable.name, resourceEntryable.id, true, true);
    resourceInput.append(option).trigger('change');
    resourceInput.trigger({
      type: 'select2:select',
      params: {
        data: resourceEntryable
      }
    });
  }

  updateValueForInstallments() {
    let totalValue = 0.00
    this.installmentValueInputTargets.forEach(($el) => {
      let parsedInput = parseInt($el.value.replace(',', '').replace('.', ''))
      let currentValue = parsedInput / 100.00
      return totalValue = totalValue + currentValue;
    })
    this.valueInputTarget.value = totalValue.toFixed(2);
  }

  // Required to compute the preview (backend model.simulate_fixed_entries):
  //   recurrence_type, recurrence_target, occurrence_count.
  // Optional (for display only in the preview rows): name, value_cents,
  // start_occurrence_date (the backend hardcodes Date.current in the
  // simulation anyway, but we still send it for forward compatibility).
  simulateFixedEntries() {
    if (this.formActionValue !== 'new') { return; }

    const required = {
      recurrence_type:   this.recurrenceTypeInputTarget.value,
      recurrence_target: this.recurrenceTargetInputTarget.value,
      occurrence_count:  this.occurrenceCountInputTarget.value
    };
    if (Object.values(required).some((v) => !v)) { return; }

    const params = {
      ...required,
      start_occurrence_date: this.hasStartOccurrenceDateInputTarget ? this.startOccurrenceDateInputTarget.value : '',
      name:        this.hasNameInputTarget ? this.nameInputTarget.value : '',
      value_cents: this.hasValueInputTarget ? this.parseMoney(this.valueInputTarget.value) * 100 : ''
    };

    const target = document.getElementById('turbo-simulate-fixed-entries');
    if (!target) return;
    const baseUrl = target.dataset.simulateFixedEntriesUrl;
    const query = Object.entries(params).map((it) => it.join('=')).join('&');

    get(`${baseUrl}?${query}`, { responseKind: 'turbo-stream' });
  }
}